/* -------------------------------------------------------------------------- */
/* Bornes de validation                                                        */
/* -------------------------------------------------------------------------- */
/** Nombre maximal de lignes distinctes dans une commande. */
export const MAX_ORDER_ITEMS = 50;
/** Quantite minimale et maximale par ligne. Voir docs/01, regle RM-06. */
export const MIN_ITEM_QUANTITY = 1;
export const MAX_ITEM_QUANTITY = 99;
export const MAX_ITEM_NOTE_LENGTH = 200;
export const MAX_CUSTOMER_NOTE_LENGTH = 500;
export const MAX_CANCEL_REASON_LENGTH = 500;
/**
 * Motif porte par les commandes ecartees d'une session close depuis longtemps,
 * quand le personnel n'en donne pas d'autre.
 *
 * Fige ici parce qu'il finit en base dans `cancelReason` et ressort tel quel
 * dans l'historique et le bilan : deux formulations concurrentes rendraient ces
 * abandons impossibles a distinguer d'une annulation ordinaire.
 */
export const ABANDONED_SESSION_CANCEL_REASON = 'Table abandonnée, session expirée';
export const MAX_MENU_ITEM_DESCRIPTION_LENGTH = 500;
export const MAX_TABLE_LABEL_LENGTH = 32;
/* -------------------------------------------------------------------------- */
/* Photo d'un plat                                                             */
/* -------------------------------------------------------------------------- */
/**
 * Formats acceptes au televersement d'une photo de plat.
 *
 * Le serveur verifie le format sur les premiers octets du fichier, jamais sur
 * l'extension ni sur l'en-tete `Content-Type` : un `.jpg` qui commence par
 * `<?php` n'est pas une image. Voir docs/adr/0023.
 */
export const MENU_ITEM_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Taille maximale d'une photo de plat, en octets. */
export const MENU_ITEM_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/**
 * Cote le plus long d'une photo apres redimensionnement dans le navigateur.
 *
 * La carte l'affiche sur 320 px de large au plus ; 1600 px laissent de la marge
 * pour les ecrans a forte densite sans faire voyager les megaoctets bruts d'un
 * capteur de telephone sur le reseau mobile d'une salle.
 */
export const MENU_ITEM_IMAGE_MAX_EDGE_PX = 1600;
/* -------------------------------------------------------------------------- */
/* Invitation du premier gerant. Voir docs/adr/0024.                           */
/* -------------------------------------------------------------------------- */
/**
 * Longueur du jeton d'invitation, en caracteres.
 *
 * Il voyage dans un lien de courriel et ouvre le droit de poser un mot de
 * passe : il doit etre aussi peu devinable qu'un jeton de session.
 */
export const INVITATION_TOKEN_LENGTH = 32;
/** Chemin de l'ecran d'invitation dans le back-office des restaurants. */
export const INVITATION_CLIENT_PATH_PREFIX = '/invitation';
/** Contraintes de mot de passe du personnel. Voir docs/07, section 1.5. */
export const MIN_PASSWORD_LENGTH = 10;
export const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/;
/* -------------------------------------------------------------------------- */
/* Fiscalite                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Taux normal de TVA au Benin, en pour mille. 180 vaut 18,0 %.
 *
 * C'est le taux d'un restaurant au regime REEL tant qu'on ne lui en fixe pas un
 * autre. Cadre harmonise UEMOA, taux unique -- voir docs/adr/0008.
 */
export const DEFAULT_TAX_RATE_PERMILLE = 180;
/**
 * Taux d'un restaurant au regime TPS : aucune TVA.
 *
 * Nomme plutot qu'ecrit 0 partout : la valeur est la meme qu'une exoneration,
 * la raison ne l'est pas, et un lecteur du code doit voir laquelle des deux il
 * a sous les yeux. Voir docs/adr/0022.
 */
export const TPS_TAX_RATE_PERMILLE = 0;
/* -------------------------------------------------------------------------- */
/* Pagination                                                                  */
/* -------------------------------------------------------------------------- */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 100;
/** Nombre de commandes terminees affichees dans la colonne DONE du kanban. */
export const BOARD_DONE_LIMIT = 20;
/** Nombre d'entrees dans le classement des plats les plus vendus. */
export const TOP_ITEMS_LIMIT = 10;
/** Amplitude maximale d'un bilan sur periode, en jours. */
export const MAX_REPORT_RANGE_DAYS = 92;
/* -------------------------------------------------------------------------- */
/* Durees                                                                      */
/* -------------------------------------------------------------------------- */
export const SESSION_TTL_HOURS = 24;
export const ACCESS_TOKEN_TTL_SECONDS = 15 * 60;
export const REFRESH_TOKEN_TTL_DAYS = 7;
/** Periode de la tache d'expiration des sessions, en millisecondes. */
export const SESSION_EXPIRY_JOB_INTERVAL_MS = 10 * 60 * 1000;
/**
 * Minutes au-dela desquelles un paiement encore `DECLARED` est reinterroge
 * aupres du prestataire. Une notification se perd, un convive ferme son
 * navigateur : sans cette reprise, la commande resterait `SERVED` jusqu'a
 * l'expiration de la session.
 */
export const PAYMENT_PENDING_TIMEOUT_MINUTES = 15;
/** Periode de la tache de reconciliation des paiements, en millisecondes. */
export const PAYMENT_RECONCILE_JOB_INTERVAL_MS = 2 * 60 * 1000;
/** Delai minimal entre deux ecritures de lastActivityAt, en millisecondes. */
export const SESSION_ACTIVITY_THROTTLE_MS = 60 * 1000;
/* -------------------------------------------------------------------------- */
/* Seuils d'alerte du kanban, en minutes                                       */
/* -------------------------------------------------------------------------- */
export const ORDER_WARNING_MINUTES = 10;
export const ORDER_CRITICAL_MINUTES = 20;
/* -------------------------------------------------------------------------- */
/* Jour de service                                                             */
/* -------------------------------------------------------------------------- */
/** Minutes apres minuit delimitant le jour de service. 240 = 4 h 00. */
export const DEFAULT_SERVICE_DAY_CUTOFF_MINUTES = 240;
export const DEFAULT_TIMEZONE = 'Europe/Paris';
/* -------------------------------------------------------------------------- */
/* Paiement                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Frais du canal mobile money, en pour mille, quand un restaurant n'en a pas
 * fixe d'autre. 20 vaut 2,0 %, le tarif beninois du prestataire sous cent
 * millions de FCFA de flux mensuel.
 *
 * C'est une estimation d'affichage, pas une regle de facturation : le
 * prelevement reel est celui du prestataire. Voir docs/adr/0018.
 */
export const DEFAULT_PAYMENT_FEE_RATE_PERMILLE = 20;
/** Seule devise que le prestataire d'encaissement accepte. */
export const PAYMENT_SUPPORTED_CURRENCY = 'XOF';
/**
 * Pays du solde a lire chez le prestataire.
 *
 * Il tient un solde par pays, non par operateur : au Benin, MTN, Moov et
 * Celtiis alimentent tous la ligne « Balance BJ ». C'est ce qui permet a un
 * restaurant de n'ouvrir qu'un seul compte pour les trois reseaux.
 *
 * Constante et non colonne : le produit vise le Benin, et rien d'autre
 * aujourd'hui -- meme taux de TVA (ADR 0008), meme fuseau par defaut. Le jour
 * d'une ouverture a un autre pays, ceci devient un champ de `Restaurant`.
 */
export const PAYMENT_COUNTRY_CODE = 'BJ';
/* -------------------------------------------------------------------------- */
/* En-tetes HTTP applicatifs                                                   */
/* -------------------------------------------------------------------------- */
export const SESSION_TOKEN_HEADER = 'x-session-token';
export const IDEMPOTENCY_KEY_HEADER = 'idempotency-key';
export const REQUEST_ID_HEADER = 'x-request-id';
export const REFRESH_COOKIE_NAME = 'scan2eat_refresh';
/**
 * Cookie de rafraichissement du back-office de **plateforme**.
 *
 * Le cookie est pose sur l'hote de l'API, pas sur celui de l'application qui
 * l'a demande. Les deux back-offices parlant a la meme API, un nom unique
 * faisait que la seconde connexion ecrasait la premiere : se connecter a la
 * plateforme deconnectait le back-office du restaurant au rechargement
 * suivant. L'ADR 0015 les separe pourtant expressement.
 *
 * C'est la porte utilisee qui decide du nom, non le role du compte.
 * Voir docs/07, section 1.3.
 */
export const PLATFORM_REFRESH_COOKIE_NAME = 'scan2eat_refresh_platform';
/** Les deux back-offices, du point de vue de la session. */
export const AUTH_SCOPES = ['restaurant', 'platform'];
/** Nom du cookie de rafraichissement d'un back-office. */
export function refreshCookieName(scope) {
    return scope === 'platform' ? PLATFORM_REFRESH_COOKIE_NAME : REFRESH_COOKIE_NAME;
}
/**
 * @deprecated Nom porte avant le renommage du produit (ADR 0020).
 *
 * Lu en repli le temps d'un deploiement : sans cela, tout le personnel connecte
 * au moment de la bascule se retrouverait deconnecte en plein service. A
 * retirer au deploiement suivant.
 */
export const LEGACY_REFRESH_COOKIE_NAME = 'emenu_refresh';
/**
 * Cookie portant le jeton de session du convive.
 *
 * Second support du meme jeton, a cote du `localStorage`. Un navigateur
 * mobile peut cloisonner ou vider le stockage local d'un lancement a l'autre :
 * le convive perdait alors sa commande en cours en rescannant sa table. Le
 * cookie ne change rien au cloisonnement -- il vit dans le meme navigateur que
 * le stockage local, donc ne donne acces a rien de plus.
 */
export const SESSION_COOKIE_NAME = 'scan2eat_session';
/** @deprecated Nom d'avant le renommage. Voir `LEGACY_REFRESH_COOKIE_NAME`. */
export const LEGACY_SESSION_COOKIE_NAME = 'emenu_session';
/* -------------------------------------------------------------------------- */
/* Cles de stockage navigateur                                                 */
/* -------------------------------------------------------------------------- */
export const SESSION_TOKEN_STORAGE_KEY = 'scan2eat.sessionToken';
/**
 * Jeton du QR code de la table, conserve par l'application client.
 *
 * Permet de rouvrir une session apres un paiement, qui la ferme, sans exiger
 * du convive qu'il rescanne physiquement le QR code pour commander a nouveau.
 * N'expose rien de plus : ce jeton est deja imprime sur la table, et
 * `docs/07`, section 2.6, assume le risque de sa photographie.
 */
export const QR_TOKEN_STORAGE_KEY = 'scan2eat.qrToken';
export const CART_STORAGE_KEY = 'scan2eat.cart';
export const SOUND_ENABLED_STORAGE_KEY = 'scan2eat.admin.soundEnabled';
export const DISPLAY_CONTEXT_STORAGE_KEY = 'scan2eat.displayContext';
/**
 * Anciennes cles de stockage, d'avant le renommage du produit (ADR 0020).
 *
 * Un convive attable au moment du deploiement a son panier et sa session sous
 * l'ancienne cle. Les lire en repli lui evite de tout perdre au milieu de son
 * repas. La table associe chaque cle nouvelle a son ancienne ; elle disparaitra
 * au deploiement suivant, avec `readStoredValue`.
 *
 * @deprecated A retirer une fois le deploiement de bascule passe.
 */
export const LEGACY_STORAGE_KEYS = Object.freeze({
    [SESSION_TOKEN_STORAGE_KEY]: 'emenu.sessionToken',
    [QR_TOKEN_STORAGE_KEY]: 'emenu.qrToken',
    [CART_STORAGE_KEY]: 'emenu.cart',
    [SOUND_ENABLED_STORAGE_KEY]: 'emenu.admin.soundEnabled',
    [DISPLAY_CONTEXT_STORAGE_KEY]: 'emenu.displayContext',
});
/* -------------------------------------------------------------------------- */
/* QR codes                                                                    */
/* -------------------------------------------------------------------------- */
export const QR_TOKEN_LENGTH = 16;
/** Alphabet sans I, O ni l : un token peut devoir etre recopie a la main. */
export const QR_TOKEN_ALPHABET = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
export const QR_DEFAULT_SIZE = 512;
export const QR_MIN_SIZE = 128;
export const QR_MAX_SIZE = 2048;
export const QR_DEFAULT_MARGIN = 2;
/** Prefixe de route du frontend client resolvant un QR code. */
export const QR_CLIENT_PATH_PREFIX = '/t/';
//# sourceMappingURL=index.js.map